import {
  useEffect, useMemo, useCallback, useContext, useState, ReactElement} from "react"
import {AllDataContext} from "./LoggedinMainPage"
import {AllDataDone} from "./alldata"
import {useNavigate, useParams} from "react-router-dom"
import {SalesOrder, SalesOrderLine} from "./api/salesorder"
import {PurchaseOrder, PurchaseOrderLine} from "./api/purchaseorder"
import {ArrayElement, TSAssertType} from "./tsmagic"
import {NEVER} from "./constants"
import {ISODate} from "./date"
import utils from "./utils"
import {Tuple, tuple, compareWithDirection as tupleCompareWithDirection}
from "./tuple"
import MultiSelectInOrder, {SEPARATOR} from "./MultiSelectInOrder"
import "./Lists.scss"

type ParentPrefix<T> = T extends string ? `parent.${T}` : never
type CalcPrefix<T> = T extends string ? `$calc.${T}` : never
type ParentCalcPrefix<T> = ParentPrefix<CalcPrefix<T>>

type AllDataArrayItemTypes<T extends keyof AllDataDone> =
  AllDataDone[T] extends ReadonlyArray<any>
  ? ({
    [key in keyof ArrayElement<AllDataDone[T]>]: ArrayElement<AllDataDone[T]>[key]
  }
    & (ArrayElement<AllDataDone[T]> extends {parent: any}
      ? {[key in keyof ArrayElement<AllDataDone[T]>["parent"] as ParentPrefix<key>]:
        ArrayElement<AllDataDone[T]>["parent"][key]}
      & (ArrayElement<AllDataDone[T]>["parent"] extends {"$calc": any}
        ? {[key in keyof ArrayElement<AllDataDone[T]>["parent"]["$calc"]
          as ParentCalcPrefix<key>]:
          ArrayElement<AllDataDone[T]>["parent"]["$calc"][key]}
        : {})
      : {})
    & (ArrayElement<AllDataDone[T]> extends {"$calc": any}
      ? {[key in keyof ArrayElement<AllDataDone[T]>["$calc"] as CalcPrefix<key>]:
        ArrayElement<AllDataDone[T]>["$calc"][key]}
      : {})
  )
  : never

type SupportedDefaultTypes = string | number | ISODate | null | boolean

const showableItemTypeByKey = {
  salesOrders: SalesOrder,
  salesOrderLines: SalesOrderLine,
  purchaseOrders: PurchaseOrder,
  purchaseOrderLines: PurchaseOrderLine,
} as const
type ShowableItemKey = keyof typeof showableItemTypeByKey
type ShowableItemType = typeof showableItemTypeByKey[ShowableItemKey]

const showableItemKeyByType = new Map<ShowableItemType, ShowableItemKey>(
  Object.entries(showableItemTypeByKey).map(
    ([key, type]) => [type, key] as [ShowableItemType, ShowableItemKey]))

if (Object.keys(showableItemTypeByKey).length !== showableItemKeyByType.size) {
  throw new Error("All types in showableItemKeys should be unique")
}

type TypeForShowableItemKey<K extends ShowableItemKey> = ArrayElement<AllDataDone[K]>

type ShowableItemToString<K extends ShowableItemKey, F extends keyof AllDataArrayItemTypes<K>> = (el: AllDataArrayItemTypes<K>[F],
  item: TypeForShowableItemKey<K>,
) => string

type ShowableItemFormat<K extends ShowableItemKey, F extends keyof AllDataArrayItemTypes<K>> = (el: AllDataArrayItemTypes<K>[F],
        item: TypeForShowableItemKey<K>,
        allData: AllDataDone) => string | ReactElement

type FormattedType = "number" | "date" | "longstring" | "money" | "other"

type ShowableItemBase<K extends ShowableItemKey> = {
  id: (item: TypeForShowableItemKey<K>) => string,
  filters?: {
    [key in string]: (item: TypeForShowableItemKey<K>) => boolean
  }
  note?: string | ReactElement,
  fields: Partial<{
    [F in keyof AllDataArrayItemTypes<K>]: {
      label?: string,
      formattedType?: FormattedType,
    } & (
      AllDataArrayItemTypes<K>[F] extends SupportedDefaultTypes
      ? {format?: ShowableItemFormat<K, F>} : {format: ShowableItemFormat<K, F>}
    ) & (
      {
        groupBy?: false
      } | (
        {
          groupBy: true
        } & (
          AllDataArrayItemTypes<K>[F] extends SupportedDefaultTypes
          ? {formatString?: ShowableItemToString<K, F>}
          : {formatString: ShowableItemToString<K, F>}
        )
      )
    )
  }>
}


type ShowableItem<K extends ShowableItemKey> = {
  [F in keyof ShowableItemBase<K>]: ShowableItemBase<K>[F]
}


function defaultFormat(item: SupportedDefaultTypes): string {
  return item instanceof ISODate
    ? (item === NEVER ? "never" : item.toDDMMYYYYString())
    : typeof item === "boolean" ? (item ? "yes" : "no")
    : (item ?? "").toString()
}

function defaultToString(item: SupportedDefaultTypes): string {
  return defaultFormat(item)
}

function getProperyFromElement<K extends ShowableItemKey,
  F extends keyof typeof showableItems[K]["fields"]> (
    el: TypeForShowableItemKey<K>,
    property: F,
): AllDataArrayItemTypes<K>[F] {
  let root: any = el
  let localProperty = property as string
  if (localProperty.startsWith("parent.")) {
    utils.assert("parent" in root)
    root = root.parent
    localProperty = localProperty.slice("parent.".length)
  }
  if (localProperty.startsWith("$calc.")) {
    utils.assert("$calc" in root)
    root = root.$calc
    localProperty = localProperty.slice("$calc.".length)
  }
  return root[localProperty] as (AllDataArrayItemTypes<K>[F])
}

function formatPropertyFromElement<K extends ShowableItemKey,
  F extends keyof typeof showableItems[K]["fields"]> (
    el: TypeForShowableItemKey<K>,
    property: F,
    allData: AllDataDone,
  ): string | ReactElement {
    const key = showableItemKeyByType.get(el.constructor as ShowableItemType)!
    TSAssertType<keyof typeof showableItems[typeof key]["fields"]>(property)
    // @ts-expect-error
    const format: ShowableItemFormat<K, F> =
      showableItems[key].fields[property]!.format ?? defaultFormat
    const value = getProperyFromElement(el, property)
    return format(value, el, allData)
  }

function formattedProperyTypeFromElement<K extends ShowableItemKey,
  F extends keyof typeof showableItems[K]["fields"]> (
    el: TypeForShowableItemKey<K>,
    property: F,
  ): FormattedType {
    const key = showableItemKeyByType.get(el.constructor as ShowableItemType)!
    TSAssertType<keyof typeof showableItems[typeof key]["fields"]>(property)
    if ("formattedType" in showableItems[key].fields[property]) {
      return showableItems[key].fields[property]!.formattedType!
    }
    if ("format" in showableItems[key].fields[property]) {
      return "other"
    }
    const value = getProperyFromElement(el, property)
    return (value as any) instanceof ISODate ? "date"
      : typeof value === "number" ? "number"
      : "other"
  }

function toStringPropertyFromElement<K extends ShowableItemKey,
  F extends keyof typeof showableItems[K]["fields"]> (
    el: TypeForShowableItemKey<K>,
    property: F,
  ): string {
    const key = showableItemKeyByType.get(el.constructor as ShowableItemType)!
    TSAssertType<keyof typeof showableItems[typeof key]["fields"]>(property)
    const toString: ShowableItemToString<K, F> =
      (showableItems[key].fields[property] as any).formatString ?? defaultToString
    const value = getProperyFromElement(el, property)
    return toString(value, el)
  }

function labelPropertyFromKey<K extends ShowableItemKey,
  F extends keyof typeof showableItems[K]["fields"]> (
    key: K,
    property: F
  ): string {
    // @ts-expect-error
    return showableItems[key].fields[property].label ??
      (property as string).split(".").slice(-1)[0]
  }

function fieldNameOrdersToUrlString(
  fnos: ReadonlyArray<[string, Order]>  // can't get a better type to stick :)
): string {
  return fnos.map(fno => fno.join(":::")).join(",")
}

const showableItems: {[K in ShowableItemKey]: ShowableItem<K>} = {
  salesOrders: {
    id: item => item.orderID,
    note: "Note that orders with non-fulfilled (&non-dropshipment) lines are shown",
    fields: {
      "orderNumber": {
        label: "ordernr",
        format: (orderNumber, so, allData) => (
          <a href={so.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
            SO:{orderNumber}
          </a>
        ),
        groupBy: true,
      },
      "warehouseDescription": {
        label: "warehouse",
        groupBy: true,
      },
      "$calc.priority": {
        label: "prio",
        groupBy: true,
      },
      salesOrderLines: {
        label: "nr lines",
        format: (sols) => sols.length.toString(),
        formattedType: "number",
      },
      "deliveryDate": {
        label: "requested date",
      },
      "orderedByName": {
        label: "customer",
        formattedType: "longstring",
        groupBy: true,
      },
      "description": {
        formattedType: "longstring",
      }
    }
  },

  salesOrderLines: {
    id: item => item.iD,
    filters: {
      "line delivery date === never": sol => sol.deliveryDate !== NEVER,
      "order requested date === never": sol => sol.parent.deliveryDate !== NEVER,
    },
    note: "Note that only non-dropshipment, non completed lines are shown",
    fields: {
      "parent.orderNumber": {
        label: "ordernr",
        format: (orderNumber, sol, allData) => (
          <a href={sol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
            SO:{orderNumber}
          </a>
        ),
        groupBy: true,
      },
      "parent.warehouseDescription": {
        label: "warehouse",
        groupBy: true,
      },
      "parent.$calc.priority": {
        label: "prio",
        groupBy: true,
      },
      lineNumber: {
        label: "linenr",
      },
      quantity: {
        label: "Qty",
      },
      itemCode: {
        groupBy: true,
        label: "item",
        format: (itemCode, _sol, allData) => {
          const item = allData.items.find(item => item.code === itemCode)!
          return `${item.code} - ${item.description}`
        },
        formattedType: "longstring",
      },
      deliveryDate: {
        label: "line delivery date",
        groupBy: true,
      },
      "parent.deliveryDate": {
        label: "requested date",
      },
      "amountDC": {
        label: "\u20AC\u20AC\u20AC",
        format: item => Math.round(item).toFixed(0),
        formattedType: "money",
      },
      "$calc.warnIfTooLarge": {
        label: "warn \u20AC",
        groupBy: true,
      },
      "parent.orderedByName": {
        label: "customer",
        formattedType: "longstring",
        groupBy: true,
      },
      "parent.description": {
        label: "order description",
        formattedType: "longstring",
      },
    }
  },

  purchaseOrders: {
    id: item => item.purchaseOrderID,
    note: "Note that orders with non-fulfilled (&non-dropshipment) lines are shown",
    fields: {
      "orderNumber": {
        label: "ordernr",
        format: (orderNumber, po, allData) => (
          <a href={po.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
            SO:{orderNumber}
          </a>
        ),
        groupBy: true,
      },
      "warehouseDescription": {
        label: "warehouse",
        groupBy: true,
      },
      purchaseOrderLines: {
        label: "nr lines",
        format: (sols) => sols.length.toString(),
        formattedType: "number",
      },
      "receiptDate": {
        label: "receipt date",
      },
      "supplierName": {
        label: "supplier",
        formattedType: "longstring",
        groupBy: true,
      },
      description: {
        formattedType: "longstring",
      },
    }
  },

  purchaseOrderLines: {
    id: item => item.iD,
    note: "Note that only non-dropshipment, non completed lines are shown",
    fields: {
      "parent.orderNumber": {
        label: "ordernr",
        format: (orderNumber, pol, allData) => (
          <a href={pol.parent.getExactOnlineUrl(allData.division).toString()}
            target="_blank" rel="noreferrer">
            PO:{orderNumber}
          </a>
        ),
        groupBy: true,
      },
      "parent.warehouseDescription": {
        label: "warehouse",
        groupBy: true,
      },
      lineNumber: {
        label: "linenr",
      },
      quantity: {
        label: "Qty",
      },
      itemCode: {
        groupBy: true,
        label: "item",
        format: (itemCode, _sol, allData) => {
          const item = allData.items.find(item => item.code === itemCode)!
          return `${item.code} - ${item.description}`
        },
        formattedType: "longstring",
      },
      receiptDate: {
        label: "line receipt date",
        groupBy: true,
      },
      description: {
        formattedType: "longstring",
      },
      "parent.supplierName": {
        label: "supplier",
        formattedType: "longstring",
        groupBy: true,
      },
      "parent.description": {
        label: "PO description",
        formattedType: "longstring",
      }
    }
  }
} as const

export default function Lists(): ReactElement {
  const allData = useContext(AllDataContext)
  const {division} = allData
  const [renderCount, setRenderCount] = useState(0)
  const navigate = useNavigate()
  const itemToShow = (useParams().itemToShow ?? "-") as ShowableItemKey
  const filter = (useParams().filter ?? "-") as FilterName<typeof itemToShow>
  type Order = "ASC" | "DESC"
  const groupByString = useParams().groupBy ?? "-"
  const groupByFieldnameOrders: ReadonlyArray<FieldNameOrder<typeof itemToShow>> =
    (groupByString === "-" ? [] : groupByString.split(","))
    .map(fieldNameOrderString => {
      const parts = fieldNameOrderString.split(SEPARATOR)
      const fieldNameOrder = [parts.slice(0, -1).join(","),
        parts.slice(-1)[0]] as [FieldName<typeof itemToShow>, Order]
      const fieldInfo = showableItems[itemToShow]?.fields[fieldNameOrder[0]]
      if (fieldInfo === undefined
        || !new Set(["ASC", "DESC"]).has(fieldNameOrder[1])) {
        return undefined
      }
      return fieldNameOrder
    }).filter(utils.notNullOrUndefined)

  const listPreferencesString = localStorage.getItem(LISTPREFERENCES_KEY) || "{}"
  const listPreferences = useMemo(
    () => JSON.parse(listPreferencesString || "{}") as ListPreferences,
    [listPreferencesString])

  const sortByString = useParams().sortBy ?? "-"
  const sortByFieldnameOrders: ReadonlyArray<FieldNameOrder<typeof itemToShow>> =
    (sortByString === "-" ? [] : sortByString.split(","))
    .map(fieldNameOrderString => {
      const parts = fieldNameOrderString.split(SEPARATOR)
      const fieldNameOrder = [parts.slice(0, -1).join(","),
        parts.slice(-1)[0]] as [FieldName<typeof itemToShow>, Order]
      const fieldInfo = showableItems[itemToShow]?.fields[fieldNameOrder[0]]
      if (fieldInfo === undefined
        || !new Set(["ASC", "DESC"]).has(fieldNameOrder[1])) {
        return undefined
      }
      return fieldNameOrder
    }).filter(utils.notNullOrUndefined)


  const doNavigate = useCallback(
    ({newShowableItemKey, newFilter, newGroupBy, newSortBy, replace}:
      DoNavigateParams
      ) => {
      if (newShowableItemKey && newFilter === undefined) {
        newFilter = listPreferences[newShowableItemKey]?.filter ?? "-"
      }
      if (newShowableItemKey && newGroupBy === undefined) {
        newGroupBy = fieldNameOrdersToUrlString(
          listPreferences[newShowableItemKey]?.groupBy ?? [])
      }
      if (newGroupBy === "") {
        newGroupBy = "-"
      }
      if (newShowableItemKey && newSortBy === undefined) {
        newSortBy = fieldNameOrdersToUrlString(
          listPreferences[newShowableItemKey]?.sortBy ?? [])
      }
      if (newSortBy === "") {
        newSortBy = "-"
      }
      navigate([
        "",
        "division",
        division,
        "Lists",
        "itemToShow",
        newShowableItemKey ?? itemToShow,
        newFilter ?? filter,
        "groupBy",
        newGroupBy ?? groupByString,
        "sortBy",
        newSortBy ?? sortByString,
      ].map(encodeURIComponent).join("/"), {replace: replace ?? false})
    }, [navigate, division, itemToShow, groupByString, sortByString,
      filter, listPreferences])

  useEffect(() => {
    if (itemToShow in showableItems) {
      return;
    }
    doNavigate({newShowableItemKey: "salesOrderLines", replace: true})
  }, [itemToShow, doNavigate])
  return render(allData, itemToShow, groupByFieldnameOrders, sortByFieldnameOrders,
    filter, doNavigate, listPreferences, () => setRenderCount(renderCount + 1))
}

type DoNavigateParams = {
  newShowableItemKey?: ShowableItemKey,
  newGroupBy?: string,
  newFilter?: string,
  newSortBy?: string,
  replace?: boolean
}

type FieldName<ItemToShow extends ShowableItemKey> =
  keyof typeof showableItems[ItemToShow]["fields"] & string
type Order = "ASC" | "DESC"
type FieldNameOrder<ItemToShow extends ShowableItemKey> =
  [FieldName<ItemToShow>, Order]
type FilterName<ItemToShow extends ShowableItemKey> =
  keyof typeof showableItems[ItemToShow]["filters"] & string

const LISTPREFERENCES_KEY = "ListPreferences"
type ItemPreference<ItemToShow extends ShowableItemKey> = {
    filter: FilterName<ItemToShow>
    groupBy: ReadonlyArray<FieldNameOrder<ItemToShow>>
    sortBy: ReadonlyArray<FieldNameOrder<ItemToShow>>
}
type ListPreferences = Partial<{
  [ItemToShow in ShowableItemKey]: ItemPreference<ItemToShow>
}>

function render<ItemToShow extends ShowableItemKey>(
  allData: AllDataDone,
  itemToShow: ItemToShow,
  groupByFieldnameOrders: ReadonlyArray<FieldNameOrder<ItemToShow>>,
  sortByFieldnameOrders: ReadonlyArray<FieldNameOrder<ItemToShow>>,
  filter: FilterName<ItemToShow>,
  doNavigate: (params: DoNavigateParams) => void,
  listPreferences: ListPreferences,
  rerender: () => void,
): ReactElement {
  function saveAsPreferences() {
    const newListPreferences = {
      ...listPreferences,
      [itemToShow]: ({
        filter: filter,
        groupBy: groupByFieldnameOrders,
        sortBy: sortByFieldnameOrders,
      } as ItemPreference<ItemToShow>)
    }
    localStorage.setItem(LISTPREFERENCES_KEY, JSON.stringify(newListPreferences))
    rerender()
  }

  const showingCurrentPreferences = (() => {
    if (!(itemToShow in listPreferences)) {
      return filter === "-"
        && groupByFieldnameOrders.length === 0
        && sortByFieldnameOrders.length === 0
    }
    return filter === listPreferences[itemToShow]!.filter
      && fieldNameOrdersToUrlString(groupByFieldnameOrders) === (
        fieldNameOrdersToUrlString(listPreferences[itemToShow]!.groupBy))
      && fieldNameOrdersToUrlString(sortByFieldnameOrders) === (
        fieldNameOrdersToUrlString(listPreferences[itemToShow]!.sortBy))
  })()

  if (!showableItems[itemToShow]) {
    return <div>Loading...</div>
  }

  type ItemType = TypeForShowableItemKey<typeof itemToShow>

  const fieldInfoByFieldName = showableItems[itemToShow].fields
  const groupSortOrderReverse = groupByFieldnameOrders.map(
    ([, order]) => order === "DESC")
  const filters = showableItems[itemToShow].filters
  const groupedItems = (() => {
    return new Map([...utils.intoBuckets<ItemType, Tuple<readonly any[]>>(
      (allData[itemToShow] as ReadonlyArray<ItemType>)
      .filter(((filters && filters[filter]) || (() => true)) as (x: any) => boolean)
      , el => tuple(groupByFieldnameOrders.map(
        ([fieldName]) => {
          const value = getProperyFromElement(el, fieldName)
          if (!((value as any) instanceof ISODate
            || typeof value === "string"
            || typeof value === "number"
            || typeof value === "boolean"
            || value === null)) {
            throw new Error("Don't know what to do with value for " + fieldName)
          }
          return value
        }))).entries()]
      .sort(utils.By(
        ([tup]) => tup, tupleCompareWithDirection(groupSortOrderReverse))))
  })()
  const sortSortOrderReverse = sortByFieldnameOrders.map(
    ([, order]) => order === "DESC")
  groupedItems.forEach((items, key) => {
    groupedItems.set(key, [...items]
      .sort(utils.By(el => tuple(
        [...sortByFieldnameOrders, ...Object.entries(fieldInfoByFieldName)].map(
        ([fieldName]) => {
          const value = getProperyFromElement(
            el, fieldName as FieldName<ItemToShow>)
          if (!((value as any) instanceof ISODate
            || typeof value === "string"
            || typeof value === "number"
            || typeof value === "boolean"
            || value === null)) {
            return null // for now, ignore
          }
          return value
        })), tupleCompareWithDirection(sortSortOrderReverse))))
  })

  return <div className="Lists">
    <div className="selectHeader">
      <div className="itemToShowSelector">
        <span>Select what to show a list of: </span>
        <select data-cy="select-item-to-show" value={itemToShow} onChange={
          e => doNavigate({newShowableItemKey: e.target.value as ShowableItemKey})}>
          {Object.keys(showableItems).map(
            itemToShow => <option key={itemToShow} value={itemToShow}>
              {itemToShow}
            </option>
          )}
        </select>
        <span>filter out:
          <select data-cy="select-filter" value={filter}
            onChange={e => doNavigate({newFilter: e.target.value})}>
            <option value="-">nothing</option>
            {utils.keysOrUndefined(showableItems[itemToShow].filters)?.map(
              (name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </span>
        <span className="note">{showableItems[itemToShow].note}</span>
      </div>
      <div className="groupBySelector">
        <span>Group by: </span>
        <MultiSelectInOrder
          value={groupByFieldnameOrders.map(fo => fo.join(":::"))}
          options={new Map(Object.entries(fieldInfoByFieldName)
            .filter(([_fieldName, fieldInfo]) => fieldInfo.groupBy ?? false)
            .flatMap(([fieldName, _fieldInfo]): ReadonlyArray<[string, string]> =>
            [["ASC", '\u25B2'], ["DESC", "\u25BC"]]
            .map(([ord, order]): [string, string] => [
            fieldName + SEPARATOR + ord,
            `${order} ${labelPropertyFromKey(
              itemToShow, fieldName as FieldName<ItemToShow>)}`
            ])))}
          onChange={newValue => doNavigate({newGroupBy: newValue.join(",")})}
        />
      </div>
      <div className="sortBySelector">
        <span>Sort by: </span>
        <MultiSelectInOrder
          value={sortByFieldnameOrders.map(fo => fo.join(":::"))}
          options={new Map(Object.entries(fieldInfoByFieldName)
            .filter(([fieldName, _fieldInfo]) =>
            groupByFieldnameOrders.find(([f]) => fieldName === f) === undefined)
            .flatMap(([fieldName, _fieldInfo]): ReadonlyArray<[string, string]> =>
            [["ASC", '\u25B2'], ["DESC", "\u25BC"]]
            .map(([ord, order]): [string, string] => [
            fieldName + SEPARATOR + ord,
            `${order} ${labelPropertyFromKey(
              itemToShow,fieldName as FieldName<ItemToShow>)}`
            ])))}
          onChange={newValue => doNavigate({newSortBy: newValue.join(",")})}
        />
      </div>
      <button className={"saveAsPreferences " + (
        showingCurrentPreferences ? "current" : "new")}
        onClick={() => saveAsPreferences()}
        disabled={showingCurrentPreferences}>
        {showingCurrentPreferences ? <>Already saved as preferences</>
        : <>Save as preferences</>}
      </button>
    </div>
    <div className="listtable">
      <table>
        <thead>
          <tr>
            {Object.entries(fieldInfoByFieldName)
              .filter(([fieldName]) =>
              groupByFieldnameOrders.find(([f]) => fieldName === f) === undefined)
              .map(([fieldName]) => {
                const fno = sortByFieldnameOrders.find(([f]) => fieldName === f)
                return [fieldName, (fno ?? [])[1] as (Order | undefined)] as (
                [FieldName<ItemToShow>, Order | undefined])
            })
            .map(([fieldName, order]) => <th key={fieldName}
              onClick={() => doNavigate({newSortBy:
                `${fieldName}${SEPARATOR}${order === "ASC" ? "DESC" : "ASC"}`})}
              className={order ? `order-${order}` : ""}>
              {labelPropertyFromKey(itemToShow, fieldName)}
            </th>)}
          </tr>
        </thead>
        {[...groupedItems.values()].map(
          items => <tbody key={groupByFieldnameOrders.map(([groupByName]) => (
            labelPropertyFromKey(itemToShow, groupByName)
            + ": " + toStringPropertyFromElement(items[0], groupByName))).join(", ")}>
            {groupByFieldnameOrders.length > 0 && <tr>
              <th className="grouping" colSpan={
                Object.keys(fieldInfoByFieldName).length
                - groupByFieldnameOrders.length}>
                {groupByFieldnameOrders.map(
                  ([fieldName]) => <span className="group" key={fieldName}>
                    {labelPropertyFromKey(itemToShow, fieldName)}: {
                      formatPropertyFromElement(items[0], fieldName, allData)}
                  </span>)}
              </th>
            </tr>}
            { //@ts-ignore
              items.map(item => <tr key={showableItems[itemToShow].id(item as ItemType)}>
                { //@ts-ignore
                  (Object.keys(fieldInfoByFieldName) as ReadonlyArray<FieldName>)
                  .filter(fieldName =>
                  groupByFieldnameOrders.find(([f]) => fieldName === f) === undefined)
                  .map(fieldName => <td key={fieldName}
                    className={formattedProperyTypeFromElement(item, fieldName)}>
                    <span className="listfield">
                      {formatPropertyFromElement(item, fieldName, allData)}
                    </span>
                  </td>)}
              </tr>)}
          </tbody>)
        }
      </table>
    </div>
  </div>
}
